// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// THE OTHER REGISTER — vehicles, weighted by how many cars each campaign
// actually touches.
//
// This is the only half of the corpus that publishes a MAGNITUDE. On the
// consumer side a recall of four units and a recall of a million rank
// identically, because the ACCC publishes no unit count at all. Here the
// Department of Infrastructure does, and weighting by it is the entire reason
// this register is worth crawling separately.
//
// It is kept structurally and visually apart from the consumer register: the
// two share no identifier, no category vocabulary and no common date meaning,
// so a combined total would be a number about nothing.

import { loadVehicles } from '../data';
import { esc, num, shortDate, truncEsc } from '../format';
import { closeButton, openOverlay } from '../overlay';
import { chartFrame, labelChart, markTip, svgEl } from '../components/svg';
import { glossaryButton } from '../glossary';
import type { VehiclePayload, VehicleRow } from '../types';

const TOP_MAKES = 26;

export async function renderVehicles(host: HTMLElement): Promise<void> {
  host.innerHTML = '<div class="loading">Loading the vehicle register…</div>';
  const data = await loadVehicles();

  const unitsKnownPct = (100 * data.rows.filter((r) => r.units != null).length) / (data.rows.length || 1);
  const totalUnits = data.rows.reduce((a, r) => a + (r.units ?? 0), 0);
  const biggest = [...data.rows].sort((a, b) => (b.units ?? 0) - (a.units ?? 0)).slice(0, 12);

  host.innerHTML = `
    <div class="view-head">
      <h2>Vehicle recalls</h2>
      <p>A separate register, run by the Department of Infrastructure rather than the ACCC —
      ${num(data.rows.length)} campaigns. Unlike the consumer register it publishes
      ${glossaryButton('affectedUnits', 'affected units')}, so these recalls can be weighted by how many
      vehicles each one touches — rather than counting a four-vehicle recall the same as a million-vehicle
      one. Those figures sum to ${num(totalUnits)} across all campaigns${unitsKnownPct >= 99.5 ? '' : ` (the ${unitsKnownPct.toFixed(0)}% that state a figure)`},
      which is <strong>not</strong> a count of distinct vehicles: a car recalled three times is counted
      three times.</p>
    </div>

    <div class="note" style="margin-bottom:var(--space-lg)">
      <strong>These are not the same register as the consumer recalls elsewhere on this site</strong> —
      different regulator, different categories, different identifiers. They are never added together here,
      and a combined “total recalls” figure would not mean anything. A small number of these campaigns are
      also listed by the ACCC, matched by their REC number; the build checks that every such cross-listing
      resolves to a campaign in this register.
    </div>

    <div class="controls">
      <div class="control grow" style="flex:1 1 320px">
        <label class="field-label" for="vq">Search by make, model, variant or campaign number</label>
        <input type="search" id="vq" placeholder="e.g. Toyota, Ranger, airbag, REC-006638" autocomplete="off" />
      </div>
    </div>
    <div id="vresults" class="stack" style="margin-bottom:var(--space-2xl)"></div>

    <div id="matrix"></div>
    <div id="biggest" style="margin-top:var(--space-lg)"></div>
  `;

  const input = host.querySelector<HTMLInputElement>('#vq')!;
  const results = host.querySelector<HTMLElement>('#vresults')!;

  const drawResults = (q: string) => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) {
      results.innerHTML = '<p class="small muted">Type to search the vehicle register. The charts below cover all campaigns.</p>';
      return;
    }
    const hits = data.rows.filter((r) => {
      const hay = `${r.make} ${r.model} ${r.id} ${r.haystack}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    }).sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

    if (!hits.length) {
      results.innerHTML = `<div class="empty-state"><p><strong>No vehicle recall matches “${esc(q)}”.</strong></p>
        <p class="small">Check the <a href="https://www.vehiclerecalls.gov.au/" target="_blank" rel="noopener">official vehicle register</a>,
        which can also look up a specific VIN.</p></div>`;
      return;
    }

    results.innerHTML = `<p class="small muted">${num(hits.length)} matching campaign${hits.length === 1 ? '' : 's'}</p>`;
    for (const r of hits.slice(0, 40)) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'card';
      card.style.cssText = 'text-align:left;cursor:pointer;width:100%;display:block';
      card.innerHTML = `
        <div class="row" style="align-items:flex-start">
          <div class="grow">
            <div class="card-title">${esc(r.make)} ${truncEsc(r.model, 90)}</div>
            <div class="card-sub">${esc(r.id)} · ${esc(shortDate(r.date))}${r.yearFrom ? ` · ${r.yearFrom}–${r.yearTo}` : ''}</div>
          </div>
          <span class="pill shrink0 mono">${r.units != null ? `${num(r.units)} vehicles` : 'units not stated'}</span>
        </div>`;
      card.addEventListener('click', () => openVehicle(r));
      results.append(card);
    }
  };

  let timer: number | undefined;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => drawResults(input.value), 300);
  });
  drawResults('');

  host.querySelector<HTMLElement>('#matrix')!.append(makeYearMatrix(data));
  host.querySelector<HTMLElement>('#biggest')!.append(biggestCampaigns(biggest));
}

/**
 * Make × model-year, because a campaign covers a RANGE of model years.
 *
 * A range is an interval, not a point, so the natural form is a cell per
 * (make, year) that the campaign spans — which a bar chart of campaigns per
 * make cannot express at all. Units are summed per cell as an UPPER BOUND and
 * the tooltip says so: the register never states how many of a campaign's
 * vehicles fall in each individual model year, so a cell's units figure is the
 * size of the campaigns touching that year, not a count of cars built that year.
 */
function makeYearMatrix(data: VehiclePayload): HTMLElement {
  const makes = data.topMakes.slice(0, TOP_MAKES).map((m) => m.make);
  const makeSet = new Set(makes);
  const cells = data.makeYear.filter((c) => makeSet.has(c.make));
  if (!cells.length) {
    const el = document.createElement('div');
    el.className = 'empty-state';
    el.textContent = 'No model-year information is available.';
    return el;
  }

  const minYear = Math.max(1990, Math.min(...cells.map((c) => c.year)));
  const maxYear = Math.max(...cells.map((c) => c.year));
  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) years.push(y);

  const LABEL_W = 132;
  const CW = 22;
  const RH = 22;
  const HEAD = 34;
  const W = LABEL_W + years.length * CW + 16;
  const H = HEAD + makes.length * RH + 16;

  const { figure, mount } = chartFrame({
    title: 'Which model years get recalled',
    subtitle: 'Rows are makes (the 26 with the most vehicles affected), columns are model years. Colour is how many vehicles the campaigns covering that year affect.',
    scroll: true,
    minWidth: 700,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMinYMin meet' });
  svg.style.minWidth = `${W}px`;
  labelChart(svg, 'Vehicle recalls by make and model year',
    'A heatmap of makes against model years, shaded by the number of vehicles affected.');

  const byKey = new Map(cells.map((c) => [`${c.make}|${c.year}`, c]));
  const maxUnits = Math.max(...cells.map((c) => c.units), 1);

  years.forEach((y, i) => {
    if (y % 5 !== 0) return;
    const t = svgEl('text', { x: LABEL_W + i * CW + CW / 2, y: HEAD - 10, class: 'axis-text', 'text-anchor': 'middle' });
    t.textContent = String(y);
    svg.append(t);
  });

  makes.forEach((make, r) => {
    const y0 = HEAD + r * RH;
    const label = svgEl('text', { x: LABEL_W - 8, y: y0 + RH / 2 + 4, class: 'axis-text', 'text-anchor': 'end' });
    label.textContent = make.length > 18 ? `${make.slice(0, 17)}…` : make;
    svg.append(label);

    years.forEach((yr, i) => {
      const c = byKey.get(`${make}|${yr}`);
      const g = svgEl('g', { class: 'mark' });
      g.append(svgEl('rect', {
        x: LABEL_W + i * CW + 1, y: y0 + 1, width: CW - 2, height: RH - 2, rx: 2,
        fill: c ? shade(c.units / maxUnits) : 'var(--bg-elevated)',
        stroke: 'var(--bg-base)', 'stroke-width': 0.8,
      }));
      if (c) {
        markTip(g,
          `${make} — model year ${yr}\n${num(c.campaigns)} campaign${c.campaigns === 1 ? '' : 's'} cover this year\n`
          + `Up to ${num(c.units)} vehicles affected across those campaigns\n`
          + '(campaigns span several model years; the register does not split units between them)',
          `${make}, model year ${yr}: ${num(c.campaigns)} campaigns, up to ${num(c.units)} vehicles`);
      }
      svg.append(g);
    });
  });

  mount.append(svg);
  return figure;
}

function shade(t: number): string {
  const k = Math.sqrt(Math.max(0, Math.min(1, t)));
  const from = [242, 239, 233];
  const to = [143, 39, 8];
  const c = from.map((v, i) => Math.round(v + (to[i] - v) * k));
  return `rgb(${c.join(',')})`;
}

function biggestCampaigns(rows: VehicleRow[]): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `
    <div class="card-title">The largest campaigns by vehicles affected</div>
    <div class="card-sub">This is the ranking the consumer register cannot produce, because it publishes no unit counts at all.</div>
    <div class="table-scroll" style="margin-top:var(--space-md);border:none">
      <table>
        <caption class="visually-hidden">Vehicle recall campaigns ranked by number of vehicles affected</caption>
        <thead><tr>
          <th scope="col">Make and model</th>
          <th scope="col" class="num">Vehicles</th>
          <th scope="col" class="num nowrap">Published</th>
        </tr></thead>
        <tbody></tbody>
      </table>
    </div>`;
  const tbody = card.querySelector('tbody')!;
  for (const r of rows) {
    const tr = document.createElement('tr');
    tr.className = 'clickable';
    tr.innerHTML = `
      <td><div class="truncate" style="max-width:28rem"><strong>${esc(r.make)}</strong> ${truncEsc(r.model, 70)}</div></td>
      <td class="num">${num(r.units)}</td>
      <td class="num nowrap">${esc(shortDate(r.date))}</td>`;
    tr.addEventListener('click', () => openVehicle(r));
    tbody.append(tr);
  }
  return card;
}

function openVehicle(r: VehicleRow): void {
  openOverlay({
    panelClass: 'drawer',
    label: `Vehicle recall ${r.id}`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = `${r.make} ${r.model}`;
      head.append(h, closeButton(close, 'Close vehicle recall details'));

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <div class="row" style="flex-wrap:wrap;gap:var(--space-sm)">
          <span class="pill mono">${esc(r.id)}</span>
          <span class="pill">${esc(shortDate(r.date))}</span>
          ${r.yearFrom ? `<span class="pill">${r.yearFrom}–${r.yearTo}</span>` : ''}
          <span class="pill mono">${r.units != null ? `${num(r.units)} vehicles` : 'units not stated'}</span>
        </div>
        ${block('What is wrong with it', r.defect)}
        ${block('What could happen', r.hazard)}
        ${r.consumerAction ? `<div class="note"><strong>What to do</strong><div style="margin-top:.35rem;white-space:pre-line">${esc(r.consumerAction)}</div></div>` : ''}
        ${block('Supplier', r.supplier)}
        <p class="small"><a href="https://www.vehiclerecalls.gov.au/recalls/${esc(r.id.toLowerCase())}" target="_blank" rel="noopener">Read the official notice, and check a VIN ↗</a></p>
        <div class="note small">Whether a specific vehicle is affected is decided by its VIN. This site does not
        reproduce the VIN lists — use the official register or contact the manufacturer.</div>`;
      panel.append(head, body);
    },
  });
}

function block(title: string, text: string): string {
  if (!text) return '';
  return `<div><div class="stat-label">${esc(title)}</div><div style="white-space:pre-line">${esc(text)}</div></div>`;
}
